import React, { useState, useEffect, useCallback } from 'react';
import { httpsCallable } from 'firebase/functions';
import { doc, getDoc } from 'firebase/firestore';
import { functions, db, auth } from '../firebaseConfig';
import { dataService } from '../services/dataService';
import { getInboxItems, addInboxItem, updateInboxItem, deleteInboxItem } from '../utils/inboxStorage';
import { addLog } from '../utils/logsStorage';
import { renderMarkdown } from '../utils/markdown';
import { parseRepo, createIssue, createBranch, pushFile, createPR } from '../utils/githubService';
import {
  extractInboxMessages, extractRecordOps, extractGithubOps, extractGateRequests,
  stripInboxMessages, stripSpecialBlocks,
} from '../utils/inboxParsers';

const callClaude = httpsCallable(functions, 'claude');
const resolveGateCallable = httpsCallable(functions, 'resolveGate');

function buildAgentSystemPrompt(agent, extra = '') {
  const collections = agent.collections || [];
  const collectionsCtx = collections.length > 0
    ? '\n\nDATA COLLECTIONS YOU HAVE ACCESS TO:\n' +
      collections.map(c => {
        const rows = (c.records || []).slice(0, 100);
        const fieldList = (c.fields || []).map(f => {
          if (typeof f === 'string') return f;
          if (f.options?.length) return `${f.name} (allowed: ${f.options.join(' | ')})`;
          return f.name;
        }).join(', ');
        return `"${c.label}" (collection id: ${c.id}) — ${rows.length} records${fieldList ? ` (fields: ${fieldList})` : ''}\n` +
          rows.map((r, i) => `  ${i + 1}. ${JSON.stringify(r)}`).join('\n');
      }).join('\n\n')
    : '';

  const opsGuide = collections.length > 0
    ? '\n\nUPDATING RECORDS — use exactly this format:\n' +
      '<RECORD_OP action="update" collectionId="COLLECTION_ID" recordId="RECORD_ID">{"Field Name": "new value"}</RECORD_OP>\n' +
      '<RECORD_OP action="create" collectionId="COLLECTION_ID">{"Field Name": "value"}</RECORD_OP>\n' +
      '<RECORD_OP action="delete" collectionId="COLLECTION_ID" recordId="RECORD_ID" />\n' +
      'Use the exact collection id from above (e.g., ' + collections[0].id + '). ' +
      'Use exact field names from the records. Status values must match what is already used in the records — do not invent new values.'
    : '';

  const inboxGuide = `\n\nMESSAGING THE USER:\nThis platform intercepts <INBOX_MESSAGE> and delivers it to the user automatically — no email service needed. You MUST use this for any report, summary, or data the user requests. Never say you cannot send information.\n\nFormatting rules:\n- Use proper markdown pipe tables with a header separator row: | Col | Col |\n|-----|-----|\n| val | val |\n- Keep table cells SHORT (1 sentence max). Never put multi-line text in a table cell.\n- Use bullet lists below the table for longer details if needed.\n\n<INBOX_MESSAGE subject="Your subject">\nBody here.\n</INBOX_MESSAGE>\n\nWhen processing requests (record updates, approvals), be brief. Do not list processing steps. Confirm the action in 1-2 sentences only.`;

  const gh = agent.github;
  const githubGuide = gh?.token && gh?.repo
    ? `\n\nGITHUB REPOSITORY:\nRepo: ${gh.repo}\nDefault branch: ${gh.branch || 'main'}\n\nYou can make code changes by including GITHUB_OP tags in your response. The platform executes them automatically.\n\nAvailable actions:\n` +
      `<GITHUB_OP action="create_branch" name="feat/my-branch" from="${gh.branch || 'main'}" />\n` +
      `<GITHUB_OP action="push_file" path="src/example.js" branch="feat/my-branch" message="Add example">\n// full file content here\n</GITHUB_OP>\n` +
      `<GITHUB_OP action="create_pr" head="feat/my-branch" base="${gh.branch || 'main'}" title="My PR title">\nPR description here.\n</GITHUB_OP>\n` +
      `<GITHUB_OP action="create_issue" title="Bug: something is broken">\nIssue description here.\n</GITHUB_OP>\n\n` +
      `Rules:\n- Always create a new branch before pushing files — never push directly to ${gh.branch || 'main'}.\n` +
      `- push_file must include the COMPLETE file content, not a diff.\n` +
      `- Include GITHUB_OP tags silently — do not narrate each step to the user.`
    : '';

  return `You are ${agent.name}${agent.role ? `, ${agent.role}` : ''}, part of the "${agent.functionLabel}" team.` +
    (agent.playbook ? `\n\nTeam playbook:\n${agent.playbook}` : '') +
    collectionsCtx +
    opsGuide +
    githubGuide +
    inboxGuide +
    (extra ? `\n\n${extra}` : '') +
    '\n\nAlways answer data questions using the collection records shown above. Never claim you lack access to information that is listed there.';
}

function formatTime(ts) {
  if (!ts) return '';
  const d = new Date(ts);
  const diff = Date.now() - d;
  if (diff < 60000) return 'just now';
  if (diff < 3600000) return `${Math.floor(diff / 60000)}m ago`;
  if (diff < 86400000) return `${Math.floor(diff / 3600000)}h ago`;
  return d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' });
}

const TYPE_LABELS = {
  approval:     { label: 'Approval request', color: '#d97706', bg: '#fef3c7' },
  notification: { label: 'Message',          color: '#2563eb', bg: '#dbeafe' },
};


function getAgentRegistry(workspaceId) {
  if (!workspaceId) return [];
  try { return JSON.parse(localStorage.getItem(`projectly_agent_registry_${workspaceId}`) || '[]'); } catch { return []; }
}

function getCustomFolders(workspaceId) {
  if (!workspaceId) return [];
  try { return JSON.parse(localStorage.getItem(`projectly_custom_folders_${workspaceId}`) || '[]'); } catch { return []; }
}

function saveCustomFolders(workspaceId, folders) {
  if (!workspaceId) return;
  localStorage.setItem(`projectly_custom_folders_${workspaceId}`, JSON.stringify(folders));
}

function getFilterPrefs() {
  try { return JSON.parse(localStorage.getItem('projectly_inbox_filters') || '{}'); } catch { return {}; }
}
function saveFilterPrefs(prefs) {
  localStorage.setItem('projectly_inbox_filters', JSON.stringify(prefs));
}

export default function InboxView({ workspaceId, workspaces = [], onNavigate }) {
  const [items, setItems] = useState([]);
  const [folder, setFolder] = useState('inbox');
  const [selected, setSelected] = useState(null);
  const [notes, setNotes] = useState('');
  const [customFolders, setCustomFolders] = useState(() => getCustomFolders(workspaceId));
  const [newFolderName, setNewFolderName] = useState('');
  const [showNewFolder, setShowNewFolder] = useState(false);

  // Filter state — persisted in localStorage
  const [filterWs, setFilterWs] = useState(() => getFilterPrefs().workspace || 'all');
  const [filterType, setFilterType] = useState(() => getFilterPrefs().type || 'all');
  const [filterRead, setFilterRead] = useState(() => getFilterPrefs().readStatus || 'all');

  // Reply state
  const [showReply, setShowReply] = useState(false);
  const [replyBody, setReplyBody] = useState('');
  const [replySending, setReplySending] = useState(false);
  const [replyError, setReplyError] = useState('');

  // Compose state
  const [showCompose, setShowCompose] = useState(false);
  const [composeTo, setComposeTo] = useState('');
  const [composeSubject, setComposeSubject] = useState('');
  const [composeBody, setComposeBody] = useState('');
  const [composeSending, setComposeSending] = useState(false);
  const [composeError, setComposeError] = useState('');

  // Move-to-folder state
  const [showMoveMenu, setShowMoveMenu] = useState(false);

  // Bulk selection + right-click context menu state
  const [selectedIds, setSelectedIds] = useState(() => new Set());
  const [contextMenu, setContextMenu] = useState(null); // { item, x, y }

  const updateFilter = useCallback((key, value) => {
    const prefs = getFilterPrefs();
    prefs[key] = value;
    saveFilterPrefs(prefs);
  }, []);


  const reload = useCallback(() => {
    const all = getInboxItems();
    setItems(all);
    setSelected(prev => prev ? all.find(i => i.id === prev.id) || null : null);
  }, []);

  useEffect(() => {
    reload();
    const handler = () => reload();
    window.addEventListener('projectly_inbox_update', handler);
    return () => window.removeEventListener('projectly_inbox_update', handler);
  }, [reload]);

  // Reset thread/reply state when selection changes
  useEffect(() => { setShowReply(false); setReplyBody(''); setReplyError(''); }, [selected?.id]);

  // Workspace members for the compose "People" list — direct messages to
  // teammates (or a note to self) travel via the sendUserMessage callable.
  const userUid = auth.currentUser?.uid;
  const [composeMembers, setComposeMembers] = useState([]);
  useEffect(() => {
    if (!workspaceId || !showCompose) return;
    dataService.getWorkspaceMembers(workspaceId)
      .then(setComposeMembers)
      .catch(() => setComposeMembers([]));
  }, [workspaceId, showCompose]);

  // Resolve projectId → project name for the row chip. Items store ids, not
  // names (the project may be renamed), so look each one up once and cache.
  const [projectNames, setProjectNames] = useState({});
  useEffect(() => {
    const wanted = items
      .filter(i => i.projectId && i.workspaceId)
      .map(i => `${i.workspaceId}/${i.projectId}`)
      .filter((k, idx, arr) => arr.indexOf(k) === idx && !(k in projectNames));
    if (!wanted.length) return;
    let alive = true;
    (async () => {
      const found = {};
      for (const key of wanted) {
        const [wsId, pId] = key.split('/');
        try {
          const snap = await getDoc(doc(db, 'workspaces', wsId, 'projects', pId));
          const d = snap.exists() ? snap.data() : null;
          found[key] = d ? `${d.icon ? d.icon + ' ' : ''}${d.name || 'Project'}` : null;
        } catch { found[key] = null; }
      }
      if (alive) setProjectNames(prev => ({ ...prev, ...found }));
    })();
    return () => { alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [items]);

  // Clear bulk selection when switching folders
  useEffect(() => { setSelectedIds(new Set()); }, [folder]);

  // Close the right-click context menu on any outside click / escape
  useEffect(() => {
    if (!contextMenu) return;
    const close = () => setContextMenu(null);
    const onKey = (e) => { if (e.key === 'Escape') setContextMenu(null); };
    window.addEventListener('click', close);
    window.addEventListener('scroll', close, true);
    window.addEventListener('keydown', onKey);
    return () => {
      window.removeEventListener('click', close);
      window.removeEventListener('scroll', close, true);
      window.removeEventListener('keydown', onKey);
    };
  }, [contextMenu]);

  const filtered = items.filter(i => {
    // Folder filter
    if (folder === 'starred') { if (!i.starred || i.folder === 'archive') return false; }
    else if (folder === 'archive') { if (i.folder !== 'archive') return false; }
    else if (folder === 'sent') { if (i.folder !== 'sent') return false; }
    else if (folder === 'inbox') { if (i.folder !== 'inbox' && i.folder) return false; }
    else { if (i.folder !== folder) return false; }
    // Workspace filter — strict match (items without a workspaceId only show under "all")
    if (filterWs === 'current') { if (i.workspaceId !== workspaceId) return false; }
    else if (filterWs !== 'all') { if (i.workspaceId !== filterWs) return false; }
    // Type filter
    if (filterType === 'messages' && i.type === 'approval') return false;
    if (filterType === 'approvals' && i.type !== 'approval') return false;
    // Read status filter
    if (filterRead === 'unread' && i.read) return false;
    if (filterRead === 'read' && !i.read) return false;
    return true;
  });

  const unreadInbox = items.filter(i => !i.read && i.folder !== 'archive' && i.folder !== 'sent').length;
  const starredCount = items.filter(i => i.starred && i.folder !== 'archive').length;

  const select = (item) => {
    setSelected(item);
    setNotes('');
    setShowMoveMenu(false);
    if (!item.read) updateInboxItem(item.id, { read: true });
  };

  const toggleStar = (e, id) => {
    e.stopPropagation();
    const item = items.find(i => i.id === id);
    if (item) updateInboxItem(id, { starred: !item.starred });
  };

  const moveTo = (id, targetFolder) => {
    updateInboxItem(id, { folder: targetFolder });
    if (selected?.id === id) setSelected(null);
    setShowMoveMenu(false);
  };

  const remove = (id) => {
    deleteInboxItem(id);
    if (selected?.id === id) setSelected(null);
  };

  const setRead = (id, read) => updateInboxItem(id, { read });

  // ── Bulk selection ──────────────────────────────────────────────────────────
  const toggleSelect = (e, id) => {
    e.stopPropagation();
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const allVisibleSelected = filtered.length > 0 && filtered.every(i => selectedIds.has(i.id));

  const toggleSelectAll = () => {
    setSelectedIds(allVisibleSelected ? new Set() : new Set(filtered.map(i => i.id)));
  };

  const clearSelection = () => setSelectedIds(new Set());

  const bulkApply = async (fn) => {
    const ids = [...selectedIds];
    await Promise.all(ids.map(fn));
    if (selected && selectedIds.has(selected.id)) setSelected(null);
    clearSelection();
  };

  const bulkMarkRead   = (read) => bulkApply(id => updateInboxItem(id, { read }));
  const bulkStar       = (starred) => bulkApply(id => updateInboxItem(id, { starred }));
  const bulkMove       = (target) => bulkApply(id => updateInboxItem(id, { folder: target }));
  const bulkDelete     = () => bulkApply(id => deleteInboxItem(id));

  // ── Right-click context menu ────────────────────────────────────────────────
  const openContextMenu = (e, item) => {
    e.preventDefault();
    e.stopPropagation();
    // Clamp so the menu never overflows the viewport edge
    const x = Math.min(e.clientX, window.innerWidth - 200);
    const y = Math.min(e.clientY, window.innerHeight - 280);
    setContextMenu({ item, x, y });
  };

  const decide = (decision) => {
    if (!selected || selected.type !== 'approval') return;

    // Server-created gates must be resolved on the server — that is what
    // actually resumes the paused run. The localStorage/event path below only
    // reaches PipelineCanvas, so without this an approval made while the
    // canvas is closed left the workflow paused indefinitely.
    if (selected.workflowId && (selected.workspaceId || workspaceId)) {
      resolveGateCallable({
        workspaceId: selected.workspaceId || workspaceId,
        workflowId: selected.workflowId,
        decision,
      }).catch(err => {
        console.error('[Inbox] resolveGate failed', err);
      });
    }

    const action = selected.action || selected.subject?.replace(/^Approve:\s*/i, '') || 'Action';
    const gateLabel = selected.gateLabel || 'Approval Gate';
    const decisionEmoji = decision === 'approved' ? '✅' : '❌';
    const responseMsg = [
      `${decisionEmoji} **Gate ${decision}** — ${gateLabel}`,
      '',
      `**Action:** ${action}`,
      notes ? `**Approver notes:** ${notes}` : '',
      decision === 'approved' ? 'You may proceed with this action.' : 'Please address the feedback and resubmit if needed.',
    ].filter(Boolean).join('\n');

    // Immediately write to chat history so the agent sees the response
    if (selected.chatKey) {
      try {
        const raw = localStorage.getItem(`projectly_chat_${selected.chatKey}`);
        if (raw) {
          const chatData = JSON.parse(raw);
          const sess = chatData.sessions?.find(s => s.id === selected.sessionId);
          if (sess) {
            sess.messages.push({ id: Date.now(), role: 'assistant', text: responseMsg, suggestions: [] });
            localStorage.setItem(`projectly_chat_${selected.chatKey}`, JSON.stringify(chatData));
          }
        }
      } catch {}
      window.dispatchEvent(new CustomEvent('projectly_gate_response', {
        detail: { chatKey: selected.chatKey, sessionId: selected.sessionId, message: responseMsg },
      }));
    }

    // Queue node badge update for PipelineCanvas
    try {
      const pending = JSON.parse(localStorage.getItem('projectly_pending_approvals') || '[]');
      if (!pending.some(p => p.approvalId === selected.approvalId)) {
        pending.push({
          approvalId: selected.approvalId, gateId: selected.gateId,
          decision, notes, chatHandled: true, timestamp: new Date().toISOString(),
        });
        localStorage.setItem('projectly_pending_approvals', JSON.stringify(pending));
      }
    } catch {}

    window.dispatchEvent(new CustomEvent('projectly_inbox_decision', {
      detail: { approvalId: selected.approvalId, gateId: selected.gateId, decision, notes, chatHandled: true },
    }));

    addLog({ type: 'gate_decision', nodeLabel: selected.fromNode || 'Gate', agentName: 'User (Inbox)', detail: `${decision === 'approved' ? 'Approved' : 'Rejected'}: ${action}` });
    updateInboxItem(selected.id, { decision, read: true });
  };

  const handleReply = async () => {
    if (!replyBody.trim() || !selected) return;
    setReplyError('');
    setReplySending(true);

    const agentName = selected.from;
    const agent = await resolveAgent(workspaceId, agentName, selected.fromNode);
    const threadId = selected.threadId || selected.id;
    const subject = selected.subject?.startsWith('Re:') ? selected.subject : `Re: ${selected.subject || 'Message'}`;

    try {
      const contextMsg = selected.body ? `[Earlier message from ${agentName}]:\n${stripInboxMessages(selected.body)}\n\n[User reply]:` : '';
      const result = await callClaude({
        messages: [{ role: 'user', content: `${contextMsg}\n${replyBody.trim()}` }],
        model: 'claude-haiku-4-5-20251001',
        system: buildAgentSystemPrompt(agent, 'You are in an ongoing inbox conversation. Continue naturally.'),
        max_tokens: 2000,
        workspaceId: workspaceId || 'unknown',
        purpose: 'inbox_reply',
      });
      const raw = result.data?.content?.[0]?.text || 'I could not respond right now.';
      const inboxMsgs  = extractInboxMessages(raw);
      const recordOps  = extractRecordOps(raw);
      const githubOps  = extractGithubOps(raw);
      const gateReqs   = extractGateRequests(raw);
      const stripped   = stripSpecialBlocks(raw);
      const displayText = stripped || (inboxMsgs.length > 0 ? '📥 Report sent to your inbox.' : raw);

      // Apply record + GitHub ops (fire and forget)
      if (recordOps.length) applyRecordOps(workspaceId, recordOps);
      if (githubOps.length) applyGithubOps(workspaceId, githubOps);

      // Sent copy
      addInboxItem({
        id: `inbox_reply_sent_${Date.now()}`,
        type: 'notification', subject,
        from: 'You', fromNode: agent.functionLabel || '',
        preview: replyBody.trim().slice(0, 100),
        body: replyBody.trim(),
        timestamp: new Date().toISOString(),
        read: true, starred: false, folder: 'sent', threadId,
        workspaceId: workspaceId || null,
      });

      // Gate requests become approval inbox items
      for (const req of gateReqs) {
        addInboxItem({
          id: `inbox_approval_${Date.now()}_${Math.random().toString(36).slice(2, 5)}`,
          type: 'approval',
          subject: `Approve: ${req.action}`,
          from: agent.name, fromNode: agent.functionLabel || '',
          preview: req.action,
          body: req.description || req.action,
          gateId: req.gateId, gateLabel: req.gateLabel, action: req.action,
          approvalId: `approval_${Date.now()}`,
          timestamp: new Date().toISOString(),
          read: false, starred: false, folder: 'inbox', threadId,
          workspaceId: workspaceId || null,
        });
      }

      // When the agent sent INBOX_MESSAGE blocks those ARE the reply — skip the plain-text
      // agent reply item to avoid a noisy duplicate in the inbox.
      // Also skip if stripped text is empty (agent only sent ops/gate requests).
      if (inboxMsgs.length === 0 && stripped) {
        addInboxItem({
          id: `inbox_reply_recv_${Date.now()}`,
          type: 'notification', subject,
          from: agent.name, fromNode: agent.functionLabel || '',
          preview: displayText.slice(0, 100),
          body: displayText,
          timestamp: new Date().toISOString(),
          read: false, starred: false, folder: 'inbox', threadId,
          workspaceId: workspaceId || null,
        });
      }

      // Any INBOX_MESSAGE blocks become their own inbox items
      for (const msg of inboxMsgs) {
        await addInboxItem({
          id: `inbox_report_${Date.now()}_${Math.random().toString(36).slice(2, 5)}`,
          type: 'notification',
          subject: msg.subject,
          from: agent.name, fromNode: agent.functionLabel || '',
          preview: msg.body.slice(0, 120),
          body: msg.body,
          timestamp: new Date().toISOString(),
          read: false, starred: false, folder: 'inbox', threadId,
          workspaceId: workspaceId || null,
        });
      }

      addLog({ type: 'chat', nodeLabel: agent.functionLabel || agent.name, agentName: agent.name, detail: `Inbox reply: ${subject}` });
      setShowReply(false);
      setReplyBody('');
    } catch (err) {
      setReplyError(`Reply failed: ${err?.message || 'Unknown error'}`);
    }
    setReplySending(false);
  };

  const handleComposeSend = async () => {
    if (!composeTo || !composeBody.trim()) {
      setComposeError('Please select a recipient and write a message.');
      return;
    }
    setComposeError('');
    setComposeSending(true);

    // Direct message to a workspace member — delivered by the server
    // callable (their inbox is not client-writable), sent copy kept locally.
    if (composeTo.startsWith('user:')) {
      const toUid = composeTo.slice(5);
      const dmSubject = composeSubject.trim() || 'Message';
      try {
        const res = await dataService.sendUserMessage(
          workspaceId, toUid, dmSubject, composeBody.trim()
        );
        if (toUid !== userUid) {
          addInboxItem({
            id: `inbox_sent_${Date.now()}`,
            type: 'notification', subject: dmSubject,
            from: 'You', fromNode: 'Direct message',
            preview: composeBody.trim().slice(0, 100),
            body: composeBody.trim(),
            timestamp: new Date().toISOString(),
            read: true, starred: false, folder: 'sent',
            threadId: res?.threadId, toUid,
            workspaceId: workspaceId || null,
          });
        }
        setShowCompose(false);
        setComposeTo(''); setComposeSubject(''); setComposeBody('');
      } catch (err) {
        setComposeError(`Send failed: ${err?.message || 'Unknown error'}`);
      }
      setComposeSending(false);
      return;
    }

    const agent = await resolveAgent(workspaceId, composeTo, composeTo);
    const subject = composeSubject.trim() || 'Message';
    const threadId = `thread_${Date.now()}`;

    try {
      const result = await callClaude({
        messages: [{ role: 'user', content: composeBody.trim() }],
        model: 'claude-haiku-4-5-20251001',
        system: buildAgentSystemPrompt(agent, 'You are receiving a new message from the user.'),
        max_tokens: 2000,
        workspaceId: workspaceId || 'unknown',
        purpose: 'inbox_compose',
      });
      const raw = result.data?.content?.[0]?.text || 'I could not respond right now.';
      const inboxMsgs = extractInboxMessages(raw);
      const recordOps = extractRecordOps(raw);
      const githubOps = extractGithubOps(raw);
      const gateReqs  = extractGateRequests(raw);
      const stripped  = stripSpecialBlocks(raw);
      const displayText = stripped || (inboxMsgs.length > 0 ? '📥 Report sent to your inbox.' : raw);

      // Apply record + GitHub ops (fire and forget)
      if (recordOps.length) applyRecordOps(workspaceId, recordOps);
      if (githubOps.length) applyGithubOps(workspaceId, githubOps);

      // Sent copy
      addInboxItem({
        id: `inbox_sent_${Date.now()}`,
        type: 'notification', subject,
        from: 'You', fromNode: agent.functionLabel || '',
        preview: composeBody.trim().slice(0, 100),
        body: composeBody.trim(),
        timestamp: new Date().toISOString(),
        read: true, starred: false, folder: 'sent', threadId, replyTo: agent.name,
        workspaceId: workspaceId || null,
      });

      // Gate requests become approval inbox items
      for (const req of gateReqs) {
        addInboxItem({
          id: `inbox_approval_${Date.now()}_${Math.random().toString(36).slice(2, 5)}`,
          type: 'approval',
          subject: `Approve: ${req.action}`,
          from: agent.name, fromNode: agent.functionLabel || '',
          preview: req.action,
          body: req.description || req.action,
          gateId: req.gateId, gateLabel: req.gateLabel, action: req.action,
          approvalId: `approval_${Date.now()}`,
          timestamp: new Date().toISOString(),
          read: false, starred: false, folder: 'inbox', threadId,
          workspaceId: workspaceId || null,
        });
      }

      if (inboxMsgs.length === 0 && stripped) {
        addInboxItem({
          id: `inbox_reply_${Date.now()}`,
          type: 'notification',
          subject: `Re: ${subject}`,
          from: agent.name, fromNode: agent.functionLabel || '',
          preview: displayText.slice(0, 100),
          body: displayText,
          timestamp: new Date().toISOString(),
          read: false, starred: false, folder: 'inbox', threadId,
          workspaceId: workspaceId || null,
        });
      }

      // INBOX_MESSAGE blocks as separate items
      for (const msg of inboxMsgs) {
        await addInboxItem({
          id: `inbox_report_${Date.now()}_${Math.random().toString(36).slice(2, 5)}`,
          type: 'notification',
          subject: msg.subject,
          from: agent.name, fromNode: agent.functionLabel || '',
          preview: msg.body.slice(0, 120),
          body: msg.body,
          timestamp: new Date().toISOString(),
          read: false, starred: false, folder: 'inbox', threadId,
          workspaceId: workspaceId || null,
        });
      }

      addLog({ type: 'chat', nodeLabel: agent.functionLabel || agent.name, agentName: agent.name, detail: `Inbox compose: ${subject}` });
      setShowCompose(false);
      setComposeTo(''); setComposeSubject(''); setComposeBody('');
    } catch (err) {
      setComposeError(`Send failed: ${err?.message || 'Unknown error'}`);
      addLog({ type: 'error', nodeLabel: agent.functionLabel || agent.name, agentName: agent.name, detail: `Inbox compose error: ${err?.message}` });
    }
    setComposeSending(false);
  };

  const createFolder = () => {
    const name = newFolderName.trim();
    if (!name || customFolders.includes(name)) return;
    const next = [...customFolders, name];
    setCustomFolders(next);
    saveCustomFolders(workspaceId, next);
    setNewFolderName('');
    setShowNewFolder(false);
  };

  const registry = getAgentRegistry(workspaceId);
  // Group agents by function for dropdown
  const agentsByFunction = registry.reduce((acc, a) => {
    const fn = a.functionLabel || 'Unknown';
    if (!acc[fn]) acc[fn] = [];
    acc[fn].push(a);
    return acc;
  }, {});

  // Full conversation (selected + thread) in chronological order, rendered as bubbles
  const conversation = selected
    ? (selected.threadId
        ? items.filter(i => i.threadId === selected.threadId)
            .sort((a, b) => new Date(a.timestamp) - new Date(b.timestamp))
        : [selected])
    : [];
  const isMine = (msg) => msg.from === 'You' || msg.folder === 'sent';

  const allFolders = [
    { id: 'inbox',   icon: '📥', label: 'Inbox',   badge: unreadInbox },
    { id: 'sent',    icon: '📤', label: 'Sent',     badge: 0 },
    { id: 'starred', icon: '⭐', label: 'Starred',  badge: starredCount },
    { id: 'archive', icon: '🗄', label: 'Archive',  badge: 0 },
    ...customFolders.map(f => ({ id: f, icon: '📁', label: f, badge: 0, custom: true })),
  ];

  return (
    <div className={`inbox-wrap ${selected ? 'has-selection' : ''}`}>

      {/* Folder sidebar */}
      <div className="inbox-folders">
        <div className="inbox-folders-heading">Inbox</div>
        {allFolders.map(f => (
          <button key={f.id} className={`inbox-folder-btn ${folder === f.id ? 'active' : ''}`} onClick={() => setFolder(f.id)}>
            <span className="inbox-folder-icon">{f.icon}</span>
            <span className="inbox-folder-label">{f.label}</span>
            {f.badge > 0 && <span className="inbox-folder-badge">{f.badge}</span>}
          </button>
        ))}

        {showNewFolder ? (
          <div className="inbox-new-folder-row">
            <input
              className="inbox-new-folder-input"
              placeholder="Folder name…"
              value={newFolderName}
              onChange={e => setNewFolderName(e.target.value)}
              onKeyDown={e => { if (e.key === 'Enter') createFolder(); if (e.key === 'Escape') setShowNewFolder(false); }}
              autoFocus
            />
            <button className="inbox-new-folder-ok" onClick={createFolder}>✓</button>
          </div>
        ) : (
          <button className="inbox-add-folder-btn" onClick={() => setShowNewFolder(true)}>+ New folder</button>
        )}

        <div className="inbox-folders-divider" />
        <button className="inbox-compose-btn" onClick={() => setShowCompose(true)}>✏ Compose</button>

      </div>

      {/* Email list */}
      <div className="inbox-list">
        {/* Filter bar */}
        <div className="inbox-filter-bar">
          <select className="inbox-filter-select" value={filterWs} onChange={e => { setFilterWs(e.target.value); updateFilter('workspace', e.target.value); }}>
            <option value="all">All workspaces</option>
            <option value="current">Current workspace</option>
            {workspaces.map(ws => (
              <option key={ws.id} value={ws.id}>{ws.name}</option>
            ))}
          </select>
          <select className="inbox-filter-select" value={filterType} onChange={e => { setFilterType(e.target.value); updateFilter('type', e.target.value); }}>
            <option value="all">All types</option>
            <option value="messages">Messages</option>
            <option value="approvals">Approvals</option>
          </select>
          <select className="inbox-filter-select" value={filterRead} onChange={e => { setFilterRead(e.target.value); updateFilter('readStatus', e.target.value); }}>
            <option value="all">All status</option>
            <option value="unread">Unread</option>
            <option value="read">Read</option>
          </select>
        </div>
        {/* Toolbar: select-all + bulk actions */}
        {filtered.length > 0 && (
          selectedIds.size > 0 ? (
            <div className="inbox-bulk-bar">
              <label className="inbox-bulk-check">
                <input type="checkbox" checked={allVisibleSelected} onChange={toggleSelectAll} />
                <span>{selectedIds.size} selected</span>
              </label>
              <div className="inbox-bulk-actions">
                <button title="Mark as read" onClick={() => bulkMarkRead(true)}>✓ Read</button>
                <button title="Mark as unread" onClick={() => bulkMarkRead(false)}>● Unread</button>
                <button title="Star" onClick={() => bulkStar(true)}>★</button>
                {folder !== 'archive' && <button title="Archive" onClick={() => bulkMove('archive')}>🗄 Archive</button>}
                <button className="danger" title="Delete" onClick={bulkDelete}>🗑 Delete</button>
                <button className="inbox-bulk-clear" title="Clear selection" onClick={clearSelection}>✕</button>
              </div>
            </div>
          ) : (
            <div className="inbox-list-toolbar">
              <label className="inbox-bulk-check">
                <input type="checkbox" checked={false} onChange={toggleSelectAll} />
                <span>Select all</span>
              </label>
              <span className="inbox-list-count">{filtered.length} message{filtered.length > 1 ? 's' : ''}</span>
            </div>
          )
        )}
        {filtered.length === 0 && (
          <div className="inbox-empty-list">No messages in {folder}</div>
        )}
        {filtered.map(item => {
          const t = TYPE_LABELS[item.type] || TYPE_LABELS.notification;
          const isThread = items.some(i => i.threadId === item.threadId && i.id !== item.id && item.threadId);
          const isChecked = selectedIds.has(item.id);
          return (
            <div
              key={item.id}
              className={`inbox-item ${!item.read ? 'unread' : ''} ${selected?.id === item.id ? 'active' : ''} ${isChecked ? 'selected' : ''}`}
              onClick={() => select(item)}
              onContextMenu={e => openContextMenu(e, item)}
            >
              <input
                type="checkbox"
                className="inbox-item-check"
                checked={isChecked}
                onClick={e => e.stopPropagation()}
                onChange={e => toggleSelect(e, item.id)}
              />
              <div className="inbox-item-main">
                <div className="inbox-item-row1">
                  <span className="inbox-item-from">{item.from}</span>
                  <span className="inbox-item-time">{formatTime(item.timestamp)}</span>
                </div>
                <div className="inbox-item-subject">
                  {item.subject}
                  {isThread && <span className="inbox-thread-indicator">↩ thread</span>}
                </div>
                <div className="inbox-item-preview">{cleanBody(item.preview)}</div>
                <div className="inbox-item-tags">
                  <span className="inbox-type-chip" style={{ background: t.bg, color: t.color }}>{t.label}</span>
                  {item.decision === 'approved' && <span className="inbox-type-chip" style={{ background: '#dcfce7', color: '#16a34a' }}>Approved</span>}
                  {item.decision === 'rejected' && <span className="inbox-type-chip" style={{ background: '#fee2e2', color: '#dc2626' }}>Rejected</span>}
                  {item.projectId && item.workspaceId && projectNames[`${item.workspaceId}/${item.projectId}`] && (
                    <span className="inbox-type-chip inbox-project-chip" data-testid="inbox-project-chip">
                      {projectNames[`${item.workspaceId}/${item.projectId}`]}
                    </span>
                  )}
                  {filterWs === 'all' && item.workspaceId && (() => {
                    const ws = workspaces.find(w => w.id === item.workspaceId);
                    return ws ? <span className="inbox-type-chip inbox-ws-chip">{ws.name}</span> : null;
                  })()}
                  <button className={`inbox-star-btn ${item.starred ? 'on' : ''}`} onClick={e => toggleStar(e, item.id)}>★</button>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Detail or compose panel */}
      <div className="inbox-detail">
        {showCompose ? (
          <div className="inbox-compose-panel">
            <div className="inbox-compose-hdr">
              <span className="inbox-compose-title">New message</span>
              <button className="inbox-compose-close" onClick={() => { setShowCompose(false); setComposeError(''); }}>✕</button>
            </div>
            <div className="inbox-compose-field">
              <label className="inbox-compose-label">To</label>
              <select className="inbox-compose-input" data-testid="inbox-compose-to" value={composeTo} onChange={e => setComposeTo(e.target.value)}>
                <option value="">Select a recipient…</option>
                {composeMembers.length > 0 && (
                  <optgroup label="People">
                    {composeMembers.map(m => (
                      <option key={m.uid} value={`user:${m.uid}`}>
                        {m.username || m.email || m.uid}{m.uid === userUid ? ' (you — note to self)' : ''}
                      </option>
                    ))}
                  </optgroup>
                )}
                {Object.entries(agentsByFunction).map(([fnLabel, agents]) => (
                  <optgroup key={fnLabel} label={`Agents · ${fnLabel}`}>
                    {agents.map(a => (
                      <option key={a.id} value={a.id}>
                        {a.name}{a.isManager ? ' [Manager]' : ''}{a.role ? ` — ${a.role}` : ''}
                      </option>
                    ))}
                  </optgroup>
                ))}
              </select>
            </div>
            <div className="inbox-compose-field">
              <label className="inbox-compose-label">Subject</label>
              <input className="inbox-compose-input" placeholder="Subject…" value={composeSubject} onChange={e => setComposeSubject(e.target.value)} />
            </div>
            <div className="inbox-compose-field inbox-compose-field--grow">
              <label className="inbox-compose-label">Message</label>
              <textarea className="inbox-compose-textarea" placeholder="Write your message…" value={composeBody} onChange={e => setComposeBody(e.target.value)} />
            </div>
            {composeError && <div className="inbox-compose-error">{composeError}</div>}
            <div className="inbox-compose-footer">
              <button className="inbox-compose-send" onClick={handleComposeSend} disabled={composeSending}>
                {composeSending ? 'Sending…' : '⬆ Send'}
              </button>
              <button className="inbox-compose-cancel" onClick={() => { setShowCompose(false); setComposeError(''); }}>Cancel</button>
            </div>
          </div>
        ) : !selected ? (
          <div className="inbox-detail-placeholder">
            <div className="inbox-detail-placeholder-icon">📥</div>
            <div>Select a message to read</div>
            <button className="inbox-detail-compose-cta" onClick={() => setShowCompose(true)}>✏ Compose new message</button>
          </div>
        ) : (
          <>
            <button className="inbox-mobile-back" onClick={() => setSelected(null)}>← Back to messages</button>
            <div className="inbox-detail-hdr">
              <div className="inbox-detail-subject">{selected.subject}</div>
              <div className="inbox-detail-meta">
                <span>From <strong>{selected.from}</strong></span>
                {selected.fromNode && selected.fromNode !== selected.from && (
                  <span className="inbox-detail-node"> · {selected.fromNode}</span>
                )}
                <span className="inbox-detail-ts">{new Date(selected.timestamp).toLocaleString()}</span>
              </div>
              <div className="inbox-detail-actions">
                {selected.folder !== 'sent' && selected.type !== 'approval' && (
                  <button className="inbox-action-btn reply" title="Reply" onClick={() => setShowReply(v => !v)}>↩ Reply</button>
                )}
                <button className={`inbox-action-btn ${selected.starred ? 'on' : ''}`} title="Star" onClick={e => toggleStar(e, selected.id)}>★</button>
                <div className="inbox-move-wrap">
                  <button className="inbox-action-btn" title="Move to folder" onClick={() => setShowMoveMenu(v => !v)}>📁</button>
                  {showMoveMenu && (
                    <div className="inbox-move-menu">
                      {allFolders.filter(f => f.id !== folder && f.id !== 'starred').map(f => (
                        <button key={f.id} className="inbox-move-item" onClick={() => moveTo(selected.id, f.id)}>
                          {f.icon} {f.label}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
                <button className="inbox-action-btn danger" title="Delete" onClick={() => remove(selected.id)}>🗑</button>
              </div>
            </div>

            {/* Conversation — selected message plus any thread replies, as bubbles */}
            <div className="inbox-conversation">
              {conversation.map(msg => (
                <div
                  key={msg.id}
                  className={`inbox-bubble ${isMine(msg) ? 'mine' : 'theirs'} ${msg.id === selected.id ? 'focused' : ''}`}
                >
                  <div className="inbox-bubble-meta">
                    <strong>{msg.from}</strong>
                    {msg.fromNode && msg.fromNode !== msg.from && (
                      <span className="inbox-bubble-node"> · {msg.fromNode}</span>
                    )}
                    <span className="inbox-bubble-time">{new Date(msg.timestamp).toLocaleString()}</span>
                  </div>
                  <div className="inbox-bubble-body">{renderBody(msg.body)}</div>
                </div>
              ))}
            </div>

            {/* Reply panel */}
            {showReply && (
              <div className="inbox-reply-panel">
                <div className="inbox-reply-hdr">
                  <span className="inbox-reply-label">Reply to {selected.from}</span>
                  <button className="inbox-compose-close" onClick={() => setShowReply(false)}>✕</button>
                </div>
                <textarea
                  className="inbox-reply-textarea"
                  placeholder={`Message to ${selected.from}…`}
                  value={replyBody}
                  onChange={e => setReplyBody(e.target.value)}
                  autoFocus
                />
                {replyError && <div className="inbox-compose-error">{replyError}</div>}
                <div className="inbox-compose-footer">
                  <button className="inbox-compose-send" onClick={handleReply} disabled={replySending}>
                    {replySending ? 'Sending…' : '↩ Send reply'}
                  </button>
                  <button className="inbox-compose-cancel" onClick={() => setShowReply(false)}>Cancel</button>
                </div>
              </div>
            )}

            {/* Approval panel */}
            {selected.type === 'approval' && !selected.decision && (
              <div className="inbox-approval-panel">
                <div className="inbox-approval-title">Your approval is required</div>
                <textarea
                  className="inbox-approval-notes"
                  placeholder="Add notes for the agent (optional)…"
                  value={notes}
                  onChange={e => setNotes(e.target.value)}
                />
                <div className="inbox-approval-btns">
                  <button className="inbox-approve-btn" onClick={() => decide('approved')}>✓ Approve</button>
                  <button className="inbox-reject-btn" onClick={() => decide('rejected')}>✕ Reject</button>
                </div>
              </div>
            )}

            {selected.type === 'approval' && selected.decision && (
              <div className={`inbox-decision-banner ${selected.decision}`}>
                <span>{selected.decision === 'approved' ? '✅ You approved this request' : '❌ You rejected this request'}</span>
                <span className="inbox-decision-note"> · The agent has been notified</span>
                {selected.chatKey && onNavigate && (
                  <button className="inbox-view-pipeline-btn" onClick={() => onNavigate('pipeline')}>
                    View in Pipeline →
                  </button>
                )}
              </div>
            )}
          </>
        )}
      </div>

      {/* Right-click context menu */}
      {contextMenu && (
        <div className="inbox-context-menu" style={{ top: contextMenu.y, left: contextMenu.x }} onClick={e => e.stopPropagation()}>
          <button className="inbox-ctx-item" onClick={() => { select(contextMenu.item); setContextMenu(null); }}>📖 Open</button>
          {contextMenu.item.read
            ? <button className="inbox-ctx-item" onClick={() => { setRead(contextMenu.item.id, false); setContextMenu(null); }}>● Mark as unread</button>
            : <button className="inbox-ctx-item" onClick={() => { setRead(contextMenu.item.id, true); setContextMenu(null); }}>✓ Mark as read</button>}
          <button className="inbox-ctx-item" onClick={() => { updateInboxItem(contextMenu.item.id, { starred: !contextMenu.item.starred }); setContextMenu(null); }}>
            {contextMenu.item.starred ? '☆ Unstar' : '★ Star'}
          </button>
          <div className="inbox-ctx-divider" />
          <div className="inbox-ctx-label">Move to</div>
          {allFolders.filter(f => f.id !== contextMenu.item.folder && f.id !== 'starred').map(f => (
            <button key={f.id} className="inbox-ctx-item" onClick={() => { moveTo(contextMenu.item.id, f.id); setContextMenu(null); }}>
              {f.icon} {f.label}
            </button>
          ))}
          <div className="inbox-ctx-divider" />
          <button className="inbox-ctx-item danger" onClick={() => { remove(contextMenu.item.id); setContextMenu(null); }}>🗑 Delete</button>
        </div>
      )}

    </div>
  );
}

function cleanBody(text) {
  if (!text) return '';
  // Strip full INBOX_MESSAGE blocks
  const stripped = text
    .replace(/<INBOX_MESSAGE[\s\S]*?<\/INBOX_MESSAGE>/gi, '')
    .replace(/<\/?INBOX_MESSAGE[^>]*>/gi, '')
    .trim();
  if (stripped) return stripped;
  // Entire body was one INBOX_MESSAGE block — extract the inner content
  const inner = text.match(/<INBOX_MESSAGE[^>]*>([\s\S]*?)<\/INBOX_MESSAGE>/i);
  return inner ? inner[1].trim() : text.replace(/<\/?INBOX_MESSAGE[^>]*>/gi, '').trim();
}

function renderBody(text) {
  if (!text) return null;
  return <div className="inbox-md-body">{renderMarkdown(cleanBody(text))}</div>;
}

// Write RECORD_OP results to the records subcollection.
// Supports both standard format (collectionId, JSON body) and simple format (collection label, field/value attrs).
// Field name→ID resolution happens here so agents can use human-readable names.
async function applyRecordOps(workspaceId, ops) {
  if (!ops.length || !workspaceId) return;
  try {
    const pipeline = await dataService.getPipeline(workspaceId);
    const nodes = pipeline.nodes || [];
    for (const op of ops) {
      let colNode = op.collectionId
        ? nodes.find(n => n.id === op.collectionId)
        : nodes.find(n => n.type === 'collectionNode' &&
            (n.data.label || '').toLowerCase() === (op.collectionLabel || '').toLowerCase());
      if (!colNode) continue;

      const nameToId = {};
      (colNode.data.fields || []).forEach(f => {
        if (f && typeof f === 'object' && f.id) nameToId[(f.name || f.label || '').toLowerCase()] = f.id;
      });

      if (op.action === 'update' && op.recordId) {
        const updateData = {};
        if (op.data) {
          Object.entries(op.data).forEach(([k, v]) => { updateData[nameToId[k.toLowerCase()] || k] = v; });
        } else if (op.field) {
          updateData[nameToId[op.field.toLowerCase()] || op.field] = op.value;
        }
        await dataService.patchRecord(workspaceId, colNode.id, op.recordId, updateData);
      } else if (op.action === 'create') {
        const createData = {};
        if (op.data) Object.entries(op.data).forEach(([k, v]) => { createData[nameToId[k.toLowerCase()] || k] = v; });
        await dataService.addRecord(workspaceId, colNode.id, createData);
      } else if (op.action === 'delete' && op.recordId) {
        await dataService.deleteRecord(workspaceId, colNode.id, op.recordId);
      }
    }
  } catch (e) { console.error('[applyRecordOps]', e); }
}

// Execute GITHUB_OP tags emitted by an agent response.
async function applyGithubOps(workspaceId, ops) {
  if (!ops.length || !workspaceId) return;
  let conn;
  try { conn = await dataService.getConnections(workspaceId); } catch { return; }
  const gh = conn.github || {};
  if (!gh.token) { console.warn('[applyGithubOps] GitHub not connected'); return; }
  const { owner, repo } = parseRepo(gh.repo || '');
  if (!owner || !repo) { console.warn('[applyGithubOps] GitHub repo not configured'); return; }
  const defaultBranch = gh.branch || 'main';

  for (const op of ops) {
    try {
      if (op.action === 'create_branch') {
        await createBranch(gh.token, owner, repo, op.name, op.from || defaultBranch);
        addLog({ type: 'workflow', nodeLabel: 'GitHub', agentName: 'System', detail: `Branch created: ${op.name}` });

      } else if (op.action === 'push_file') {
        const branch = op.branch || defaultBranch;
        let sha = null;
        try {
          const { readFile } = await import('../utils/githubService');
          ({ sha } = await readFile(gh.token, owner, repo, op.path, branch));
        } catch {}
        await pushFile(gh.token, owner, repo, op.path, op.body, op.message || `Update ${op.path}`, branch, sha);
        addLog({ type: 'workflow', nodeLabel: 'GitHub', agentName: 'System', detail: `Pushed ${op.path} to ${branch}` });

      } else if (op.action === 'create_pr') {
        const pr = await createPR(gh.token, owner, repo, op.title, op.body, op.head, op.base || defaultBranch);
        addLog({ type: 'workflow', nodeLabel: 'GitHub', agentName: 'System', detail: `PR created: ${pr.html_url}` });

      } else if (op.action === 'create_issue') {
        const labels = op.labels ? op.labels.split(',').map(l => l.trim()).filter(Boolean) : [];
        const issue = await createIssue(gh.token, owner, repo, op.title, op.body, labels);
        addLog({ type: 'workflow', nodeLabel: 'GitHub', agentName: 'System', detail: `Issue created: ${issue.html_url}` });
      }
    } catch (e) {
      console.error('[applyGithubOps]', op.action, e);
      addLog({ type: 'error', nodeLabel: 'GitHub', agentName: 'System', detail: `${op.action} failed: ${e.message}` });
    }
  }
}

// Always fetch from Firestore so agents get live collection data.
// localStorage registry is a last-resort fallback (it may lack collections if stale).
async function resolveAgent(workspaceId, nameOrId, fallbackFromNode) {
  try {
    const [pipeline, connections] = await Promise.all([
      dataService.getPipeline(workspaceId),
      dataService.getConnections(workspaceId),
    ]);
    const github = connections.github?.token ? connections.github : null;
    const nodes = pipeline.nodes || [];
    const edges = pipeline.edges || [];
    for (const fn of nodes.filter(n => n.type === 'functionNode')) {
      const connIds = new Set(
        edges.filter(e => e.source === fn.id || e.target === fn.id)
             .map(e => e.source === fn.id ? e.target : e.source)
      );
      const colNodesList = nodes.filter(cn => connIds.has(cn.id) && cn.type === 'collectionNode');
      const collections = await Promise.all(colNodesList.map(async (cn) => {
          const rawFields = cn.data.fields || [];
          const fieldMap = {};
          rawFields.forEach(f => {
            if (f && typeof f === 'object' && f.id) fieldMap[f.id] = f.name || f.label || f.id;
          });
          const fieldDefs = rawFields.map(f => {
            if (!f || typeof f !== 'object') return { name: String(f) };
            const def = { name: f.name || f.label || f.id };
            if (f.type === 'select' && Array.isArray(f.options) && f.options.length) {
              def.options = f.options;
            }
            return def;
          });
          const allRecs = await dataService.getRecords(workspaceId, cn.id);
          const records = allRecs.slice(0, 100).map(r => {
            const named = {};
            Object.entries(r.values || {}).forEach(([k, v]) => { named[fieldMap[k] || k] = v; });
            return { id: r.id, ...named };
          });
          return { id: cn.id, label: cn.data.label || 'Unnamed', fields: fieldDefs, records };
        }));
      for (const a of (fn.data.agents || [])) {
        if (a.name === nameOrId || `${fn.id}__${a.name}` === nameOrId) {
          return {
            id: `${fn.id}__${a.name}`,
            name: a.name, role: a.role || '', isManager: !!a.isManager,
            functionId: fn.id, functionLabel: fn.data.label || '',
            playbook: fn.data.playbook || '',
            collections,
            github,
          };
        }
      }
    }
  } catch (e) { console.error('[resolveAgent]', e); }
  // Firestore unavailable — fall back to local registry
  const registry = getAgentRegistry(workspaceId);
  return registry.find(a => a.id === nameOrId || a.name === nameOrId)
    || { name: nameOrId, role: '', isManager: false, functionLabel: fallbackFromNode || nameOrId, playbook: '', collections: [] };
}
